import PropTypes from "prop-types";

export const segmentProps = {
    id: PropTypes.any.isRequired,
    label: PropTypes.string.isRequired,
    isSelected: PropTypes.bool.isRequired,
    percentage: PropTypes.number,
    minMark: PropTypes.number,
    maxMark: PropTypes.number
};

export const audienceProps = {
    icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
    ]).isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["continuous", "discrete"]).isRequired,
    segments: PropTypes.arrayOf(PropTypes.shape(segmentProps)).isRequired,
    marks: PropTypes.arrayOf(PropTypes.string)
};

export const endash = "\u2013";

/**
 * This array exists to bridge the gap between the API's representation of audiences and the UI's.
 *
 * Each element has the following shape:
 *
 * ```
 * {
 *     id: number, must match the ID of the audience in the API database,
 *
 *     title: string, the display name to use in the UI,
 *
 *     icon: string|array<string>, the FontAwesome icons that represent this audience
 *
 *     type: string, one of "discrete"  or "continuous". If discrete, the UI will render push buttons.
 *         If "continuous", it will render a slider
 *
 *     segments: array, display info for each of the audience's segments. For continuous audiences, the segment is
 *         selected if the slider's min value <= minMark and its max value >= maxMark
 *     {
 *         id: number, must match the ID of the audience_segment in the API database,
 *
 *         label: string, the display name to use in the UI,
 *
 *         minMark: number|null, a valid index for the marks array.
 *
 *         maxMark: number|null, a valid index for the marks array.
 *
 *         lowerBound: string|null, the lower bound for this segment, currently only used when formatting the audience's summary.
 *
 *         upperBound: string|null, the upper bound for this segment, currently only used when formatting the audience's summary.
 *     },
 *
 *     marks: array<string>|null, specifies the marks on the slider for continuous audiences,
 *
 *     units: string|null, specifies the "units" to be used when formatting the audience's summary
 * }
 *
 * ```
 */
export const AUDIENCES = [
    {
        id: 2,
        title: "Gender",
        icon: ["female", "male"],
        segments: [
            { label: "Female", id: 7, icon: "female" },
            { label: "Male", id: 8, icon: "male" }
        ],
        type: "discrete"
    },
    {
        id: 3,
        title: "Age",
        icon: "calendar-alt",
        segments: [
            {
                label: `18${endash}24`,
                id: 11,
                minMark: 0,
                maxMark: 1,
                lowerBound: "18",
                upperBound: "24"
            },
            {
                label: `25${endash}34`,
                id: 10,
                minMark: 1,
                maxMark: 2,
                lowerBound: "25",
                upperBound: "34"
            },
            {
                label: `35${endash}44`,
                id: 15,
                minMark: 2,
                maxMark: 3,
                lowerBound: "35",
                upperBound: "44"
            },
            {
                label: `45${endash}54`,
                id: 14,
                minMark: 3,
                maxMark: 4,
                lowerBound: "45",
                upperBound: "54"
            },
            { label: `55+`, id: 13, minMark: 4, maxMark: 5, upperBound: "55+" }
        ],

        type: "continuous",
        marks: ["18", "25", "35", "45", "55", "55+"],
        units: "year olds"
    },
    {
        id: 1,
        title: "Income",
        icon: "dollar-sign",
        segments: [
            {
                label: `0${endash}50K`,
                id: 2,
                minMark: 0,
                maxMark: 1,
                lowerBound: "0",
                upperBound: "50K"
            },
            {
                label: `50${endash}100K`,
                id: 6,
                minMark: 1,
                maxMark: 2,
                lowerBound: "50",
                upperBound: "100K"
            },
            {
                label: `100${endash}150K`,
                id: 5,
                minMark: 2,
                maxMark: 3,
                lowerBound: "100",
                upperBound: "150K"
            },
            {
                label: "150K+",
                id: 4,
                minMark: 3,
                maxMark: 4,
                upperBound: "150K+"
            }
        ],

        type: "continuous",
        marks: ["0", "50K", "100K", "150K", "150K+"],
        units: ""
    },
    {
        id: 4,
        title: "Education",
        icon: "graduation-cap",
        segments: [
            { label: "Some HS", id: 16, minMark: 0, maxMark: 0 },
            { label: "HS/GED", id: 17, minMark: 1, maxMark: 1 },
            { label: "Some College", id: 18, minMark: 2, maxMark: 2 },
            { label: "AA/AS", id: 19, minMark: 3, maxMark: 3 },
            { label: "BA/BS+", id: 20, minMark: 4, maxMark: 4 }
        ],

        type: "continuous",
        marks: ["Some HS", "HS/GED", "Some College", "AA/AS", "BA/BS+"],
        units: ""
    }
];



// WEBPACK FOOTER //
// ./src/components/CreateTest/audienceProps.js